import {useState, useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {createStructuredSelector} from 'reselect';
import styled from '@emotion/styled';
import {keyframes} from '@emotion/react';
import {colors, fonts, mq} from 'ui/config';
import {getToken} from 'api/auth';
import {waitForOneSignal, isPushOptedIn, setPushOptIn} from 'lib/onesignal';
import {isStandalone} from 'lib/displayMode';

const SESSION_KEY = 'push-prompt-dismissed';
const SHOW_DELAY = 5000;
const CLOSE_DURATION = 240;

const readDismissed = () => {
  try {
    return sessionStorage.getItem(SESSION_KEY) === '1';
  } catch {
    return false;
  }
};

const writeDismissed = () => {
  try {
    sessionStorage.setItem(SESSION_KEY, '1');
  } catch {
    /* private mode — silently ignore */
  }
};

const slideIn = keyframes({
  from: {transform: 'translateY(-100%)'},
  to: {transform: 'translateY(0)'},
});

const slideOut = keyframes({
  from: {transform: 'translateY(0)'},
  to: {transform: 'translateY(-100%)'},
});

const Banner = styled('div')({
  position: 'fixed',
  left: 0,
  right: 0,
  top: 0,
  zIndex: 1000,
  background: colors.beige,
  color: colors.darkBrown,
  padding: `calc(16px + env(safe-area-inset-top)) 24px 16px 24px`,
  boxShadow: '0 4px 18px rgba(0, 0, 0, 0.12)',
  fontFamily: fonts.default,
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  animation: `${slideIn} 320ms cubic-bezier(0.2, 0.8, 0.2, 1)`,
  '&[data-closing="true"]': {
    animation: `${slideOut} ${CLOSE_DURATION}ms ease-in forwards`,
  },
  [mq('large')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
    textAlign: 'center',
    gap: 14,
    padding: `calc(18px + env(safe-area-inset-top)) 18px 18px 18px`,
  },
});

const BellWrap = styled('div')({
  flexShrink: 0,
  width: 44,
  height: 44,
  borderRadius: '50%',
  background: colors.white,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  [mq('large')]: {
    alignSelf: 'center',
  },
});

const Copy = styled('div')({
  flex: 1,
  fontSize: 14,
  lineHeight: '20px',
});

const Headline = styled('div')({
  fontFamily: fonts.title,
  fontWeight: 'bold',
  fontSize: 16,
  marginBottom: 2,
  color: colors.darkBrown,
});

const Actions = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  flexShrink: 0,
  [mq('large')]: {
    justifyContent: 'center',
  },
});

const LaterButton = styled('button')({
  background: 'transparent',
  border: 'none',
  color: colors.darkBrown,
  fontFamily: fonts.default,
  fontSize: 14,
  cursor: 'pointer',
  padding: '8px 12px',
  textDecoration: 'underline',
});

const EnableButton = styled('button')({
  background: colors.caramel,
  color: colors.white,
  fontFamily: fonts.title,
  fontWeight: 'bold',
  fontSize: 15,
  border: 'none',
  borderRadius: 0,
  padding: '12px 22px',
  cursor: 'pointer',
  '&:disabled': {opacity: 0.6, cursor: 'default'},
});

const BellIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke={colors.caramel}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round">
    <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

const PushNotificationPrompt = ({token}) => {
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);
  const [busy, setBusy] = useState(false);
  // « ask » propose l'activation ; « help » explique comment débloquer une autorisation
  // refusée ou une demande que le navigateur a masquée.
  const [mode, setMode] = useState('ask');
  const timeout = useRef(null);
  const closeTimeout = useRef(null);

  useEffect(() => {
    if (!token) return () => {};
    if (typeof window === 'undefined' || !('Notification' in window)) return () => {};
    // Un onglet de navigateur n'affiche la PWA que par la porte de service des tests :
    // les notifications ne sont demandées que dans l'appli installée.
    if (!isStandalone()) return () => {};
    if (Notification.permission === 'granted') return () => {};
    if (readDismissed()) return () => {};

    let cancelled = false;
    timeout.current = setTimeout(async () => {
      if (Notification.permission === 'denied') {
        if (cancelled) return;
        setMode('help');
        setVisible(true);
        return;
      }
      try {
        await waitForOneSignal();
      } catch {
        return;
      }
      if (cancelled) return;
      if (isPushOptedIn()) return;
      setVisible(true);
    }, SHOW_DELAY);

    return () => {
      cancelled = true;
      clearTimeout(timeout.current);
      clearTimeout(closeTimeout.current);
    };
  }, [token]);

  const close = () => {
    writeDismissed();
    setClosing(true);
    closeTimeout.current = setTimeout(() => setVisible(false), CLOSE_DURATION);
  };

  const handleEnable = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await setPushOptIn(true);
    } catch (e) {
      console.warn('OneSignal optIn failed', e);
    }
    setBusy(false);
    // La demande peut avoir été refusée, ou masquée par le navigateur sans réponse
    // (permission restée à « default ») : seule une autorisation accordée ferme la bannière.
    if (Notification.permission === 'granted') {
      close();
      return;
    }
    setMode('help');
  };

  if (!visible) return null;

  return (
    <Banner data-closing={closing ? 'true' : 'false'} role="dialog" aria-live="polite">
      <BellWrap aria-hidden="true">
        <BellIcon />
      </BellWrap>
      {mode === 'help' ? (
        <>
          <Copy>
            <Headline>Notifications bloquées</Headline>
            Pour recevoir les notifications, touchez l’icône à gauche de l’adresse, puis autorisez les
            notifications.
          </Copy>
          <Actions>
            <LaterButton type="button" onClick={close}>
              Fermer
            </LaterButton>
          </Actions>
        </>
      ) : (
        <>
          <Copy>
            <Headline>Activez les notifications</Headline>
            Restez informé des dernières actualités du Club Carte d’Or. Vous pourrez les désactiver à
            tout moment.
          </Copy>
          <Actions>
            <LaterButton type="button" onClick={close}>
              Plus tard
            </LaterButton>
            <EnableButton type="button" onClick={handleEnable} disabled={busy}>
              Activer
            </EnableButton>
          </Actions>
        </>
      )}
    </Banner>
  );
};

PushNotificationPrompt.propTypes = {
  token: PropTypes.string,
};

PushNotificationPrompt.defaultProps = {
  token: null,
};

export default PushNotificationPrompt |> connect(createStructuredSelector({token: getToken}));
